import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormGroup,
  Grid,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactElement, ReactNode, useContext, useState } from "react";
import { BlockType, BooleanMode } from "#next/redux/searchBuilderSlice";
import {
  ExcludeCheckbox,
  TooltipForDisabledComponent,
} from "#next/components/search-builder/Block";
import { SavedSearchIdContext } from "#next/components/search-builder/Builder";

export const Toolbar = ({ children }: { children: ReactNode }) => {
  return (
    <Grid
      container
      alignItems="center"
      wrap="nowrap"
      sx={{
        backgroundColor: "#00365b",
        p: 1,
      }}
    >
      {children}
    </Grid>
  );
};

type GroupToolbarProps = {
  searchName?: string;
  setBooleanMode: (booleanMode: BooleanMode) => void;
  booleanMode: BooleanMode;
  booleanPreview: ReactElement;
  isExcluded: boolean;
  disableExclude: boolean;
  disableExcludeReason: string | null;
  setExclude: (exclude: boolean) => void;
  createBlock: (type: BlockType) => void;
  disableAddGroupControl: boolean;
  disablePreview: boolean;
  disablePreviewReason: string | null;
  usernameBlocks: number;
  isRootGroup: boolean;
  delete: () => void;
  preview: () => void;
};

const SelectBooleanMode = ({
  booleanMode,
  setBooleanMode,
  disabled,
  usernameBlocks,
}: {
  booleanMode: BooleanMode;
  setBooleanMode: GroupToolbarProps["setBooleanMode"];
  disabled: boolean;
  usernameBlocks: number;
}) => {
  const andValue = "every block (AND)";
  const orValue = "at least one block (OR)";

  const value = booleanMode === BooleanMode.And ? andValue : orValue;

  const multipleUsernameBlocks = usernameBlocks > 1;

  return (
    <Grid container alignItems="baseline">
      <Grid item>Must match</Grid>
      <Grid item sx={{ ml: 1 }}>
        <Select
          id="boolean-select"
          value={value}
          open={disabled ? false : undefined}
          onChange={(event) =>
            booleanMode === BooleanMode.Or && multipleUsernameBlocks
              ? undefined
              : setBooleanMode(
                  event.target.value === andValue
                    ? BooleanMode.And
                    : BooleanMode.Or,
                )
          }
        >
          <MenuItem
            value={andValue}
            disableGutters
            disabled={multipleUsernameBlocks}
            sx={{
              // so that the tooltip always works
              "&.Mui-disabled": {
                pointerEvents: "auto",
                backgroundColor: "unset",
              },
            }}
          >
            <Tooltip
              placement="right"
              title={
                <>
                  <Typography variant={"caption"}>
                    boolean &quot;AND&quot;
                  </Typography>
                  {multipleUsernameBlocks && (
                    <Typography
                      variant={"caption"}
                      sx={{ fontStyle: "italic", display: "block" }}
                    >
                      Incompatible with multiple username blocks
                    </Typography>
                  )}
                </>
              }
            >
              <Grid container justifyContent="center">
                {andValue}
              </Grid>
            </Tooltip>
          </MenuItem>
          <MenuItem value={orValue}>
            <Tooltip
              placement="right"
              title={
                <Typography variant={"caption"}>
                  boolean &quot;OR&quot;
                </Typography>
              }
            >
              <Grid container justifyContent="center">
                {orValue}
              </Grid>
            </Tooltip>
          </MenuItem>
        </Select>
      </Grid>
    </Grid>
  );
};

const HoverPopover = ({ children }: { children: ReactElement }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <div>
      <Typography
        aria-owns={open ? "mouse-over-popover" : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        sx={{
          ":hover": {
            color: "orange",
            cursor: "help",
          },
        }}
      >
        See Boolean
      </Typography>
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: "none",
          maxWidth: "90vw",
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box sx={{ m: 1 }}>{children}</Box>
      </Popover>
    </div>
  );
};

export function GroupToolbar(props: GroupToolbarProps) {
  const disableEditing = useContext(SavedSearchIdContext).mode !== "edit";

  const usernameDisabled =
    props.booleanMode === BooleanMode.And && props.usernameBlocks > 0;

  return (
    <Toolbar>
      <Grid item paddingRight="1em">
        <FormControl sx={{ m: 1, minWidth: 75 }} size="small">
          <SelectBooleanMode
            booleanMode={props.booleanMode}
            setBooleanMode={props.setBooleanMode}
            disabled={disableEditing}
            usernameBlocks={props.usernameBlocks}
          />
        </FormControl>
      </Grid>
      {!disableEditing && (
        <Grid item paddingRight="1em">
          <ButtonGroup size="small" variant="contained">
            <Tooltip title="Contains a phrase (which may be a single word). Quotation marks are not needed. If you need to enter multiple words that are not part of a phrase then put each word in its own text block.">
              <Button onClick={() => props.createBlock("ExactText")}>
                Exact Text
              </Button>
            </Tooltip>
            <Tooltip
              title={
                <>
                  <span>
                    Finds all words beginning with this text. Example:
                    &quot;govern&quot; would find &quot;govern&quot;,
                    &quot;governor&quot;, &quot;government&quot;, etc...
                  </span>
                  <br />
                  <br />
                  <span>
                    Note: Using this block can slow down searches. If you know
                    the set of words you are interested in then you will see
                    better performance by adding them as individual blocks
                  </span>
                </>
              }
            >
              <Button onClick={() => props.createBlock("StartsWith")}>
                Starts With
              </Button>
            </Tooltip>
            <Tooltip title="For combining blocks together and controlling how they are combined. Groups can be nested, and the results of a group of blocks can be excluded (ie they must not be found)">
              <Button
                onClick={() => props.createBlock("Group")}
                disabled={props.disableAddGroupControl}
              >
                Group
              </Button>
            </Tooltip>
            <Tooltip title="Incorporate the results of another project into this one. Use this feature to build up libraries of reusable projects.">
              <Button onClick={() => props.createBlock("SavedSearch")}>
                Project
              </Button>
            </Tooltip>
            <Tooltip
              title={
                usernameDisabled
                  ? "Multiple usernames unsupported for AND grouping because an individual post is always posted by exactly one user."
                  : "Only include posts by this username"
              }
            >
              {/* span needed to make tooltip work - https://mui.com/material-ui/react-tooltip/#disabled-elements */}
              <span style={{ display: "flex" }}>
                <Button
                  onClick={() => props.createBlock("Username")}
                  disabled={usernameDisabled}
                >
                  Username
                </Button>
              </span>
            </Tooltip>
          </ButtonGroup>
        </Grid>
      )}
      {!props.isRootGroup && (
        <Grid item paddingRight="1em">
          <FormGroup>
            <ExcludeCheckbox
              placement="bottom"
              isChecked={props.isExcluded}
              isDisabled={props.disableExclude || disableEditing}
              onChange={(event) => props.setExclude(event.target.checked)}
              disabledReason={props.disableExcludeReason}
            />
          </FormGroup>
        </Grid>
      )}

      <Grid item paddingRight="1em">
        <TooltipForDisabledComponent
          disabledTooltipText={props.disablePreviewReason}
          normalTooltipText="Preview search results for this Group"
        >
          <Button
            size="small"
            disabled={props.disablePreview}
            variant="contained"
            sx={{
              backgroundColor: "secondary",
            }}
            onClick={props.preview}
          >
            Preview
          </Button>
        </TooltipForDisabledComponent>
      </Grid>

      <Grid item paddingRight="1em">
        <HoverPopover>{props.booleanPreview}</HoverPopover>
      </Grid>

      {!props.isRootGroup && (
        <Grid item paddingRight="1em" justifySelf="end" flexGrow={1}>
          <Box display="flex" justifyContent="end">
            <IconButton
              size="small"
              sx={{ color: "#ff8987" }}
              onClick={props.delete}
              disabled={disableEditing}
            >
              <ClearIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </Grid>
      )}
    </Toolbar>
  );
}
